import { 
  MessageSquare, 
  CheckCircle, 
  RotateCcw, 
  FileText, 
  Clock,
  BarChart,
  User,
  ListChecks
} from 'lucide-react'
import { Navigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { CentificLogo } from '../components/CentificLogo'
import { ParticleBackground } from '../components/ParticleBackground'
import { ROUTES } from '../config/routes'
import { useAuth } from '../hooks/useAuth'

export function PurposePage() {
  const { login, isAuthenticated } = useAuth();
  const { inProgress } = useMsal();

  // MSAL populates `accounts` asynchronously, so on the page load that follows
  // an Azure AD redirect isAuthenticated is briefly false. Rendering during that
  // window flashes the signed-out marketing page before the redirect below fires.
  // Same guard ProtectedRoute uses.
  if (inProgress !== InteractionStatus.None) {
    return null;
  }

  // Signed-in users have no use for the sign-in CTA on this page — send them
  // to the dashboard rather than showing a logged-out-looking marketing page.
  if (isAuthenticated) {
    return <Navigate to={ROUTES.Dashboard} replace />;
  }

  return (
    <div className="min-h-screen bg-[#0f172a] relative pt-40 pb-20 px-6">
      <ParticleBackground />
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-flex p-4 bg-pink-500/20 rounded-full mb-6">
            <MessageSquare size={64} className="text-pink-400" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 heading-font">
            FeedbackFlow
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Transform your workplace feedback process with a professional, transparent, and efficient platform designed for modern teams.
          </p>
        </div>

        {/* How It Works Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-3 heading-font">How It Works</h2>
            <p className="text-lg text-slate-400">A simple, structured process for meaningful feedback</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-amber-500/30 shadow-sm hover:shadow-lg hover:border-amber-400/50 transition-all">
              <div className="inline-flex p-3 bg-amber-500/20 rounded-xl mb-4">
                <MessageSquare size={32} className="text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">1. Request Feedback</h3>
              <p className="text-sm text-slate-400">
                Employees create feedback requests with specific topics and context for their managers.
              </p>
            </div>

            <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-emerald-500/30 shadow-sm hover:shadow-lg hover:border-emerald-400/50 transition-all">
              <div className="inline-flex p-3 bg-emerald-500/20 rounded-xl mb-4">
                <CheckCircle size={32} className="text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">2. Provide Feedback</h3>
              <p className="text-sm text-slate-400">
                Managers review requests and provide structured feedback with ratings and detailed insights.
              </p>
            </div>

            <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-blue-500/30 shadow-sm hover:shadow-lg hover:border-blue-400/50 transition-all">
              <div className="inline-flex p-3 bg-blue-500/20 rounded-xl mb-4">
                <RotateCcw size={32} className="text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">3. Iterate & Refine</h3>
              <p className="text-sm text-slate-400">
                Request revisions for clarity, enabling back-and-forth dialogue for better understanding.
              </p>
            </div>

            <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-purple-500/30 shadow-sm hover:shadow-lg hover:border-purple-400/50 transition-all">
              <div className="inline-flex p-3 bg-purple-500/20 rounded-xl mb-4">
                <FileText size={32} className="text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">4. Export & Archive</h3>
              <p className="text-sm text-slate-400">
                Generate professional PDFs of completed feedback for records and performance reviews.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-3 heading-font">Key Features</h2>
            <p className="text-lg text-slate-400">Everything you need for effective feedback management</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-6 border-2 border-slate-600/50 hover:border-slate-500/70 transition-all">
              <div className="inline-flex p-3 bg-blue-500/20 rounded-xl mb-4">
                <Clock size={28} className="text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Complete Timeline</h3>
              <p className="text-sm text-slate-400">
                Track every action with a visual timeline showing who did what and when, ensuring full transparency.
              </p>
            </div>

            <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-6 border-2 border-slate-600/50 hover:border-slate-500/70 transition-all">
              <div className="inline-flex p-3 bg-amber-500/20 rounded-xl mb-4">
                <BarChart size={28} className="text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Analytics Dashboard</h3>
              <p className="text-sm text-slate-400">
                Visualize feedback trends, completion rates, and performance metrics with comprehensive analytics.
              </p>
            </div>

            <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-6 border-2 border-slate-600/50 hover:border-slate-500/70 transition-all">
              <div className="inline-flex p-3 bg-slate-500/20 rounded-xl mb-4">
                <ListChecks size={28} className="text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Status Management</h3>
              <p className="text-sm text-slate-400">
                Clear status indicators keep everyone informed: In Progress, Revision Requested, or Completed.
              </p>
            </div>
          </div>
        </section>

        {/* User Roles Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-3 heading-font">User Roles</h2>
            <p className="text-lg text-slate-400">Tailored experiences for employees and managers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-blue-900/30 border-2 border-blue-500/30 rounded-2xl p-8 backdrop-blur-sm">
              <div className="inline-flex p-3 bg-blue-500 rounded-xl mb-4">
                <User size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">For Employees</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Create feedback requests with specific topics and details</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Track request status and view submitted feedback</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Request revisions for additional clarity or detail</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">View complete feedback history and analytics</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Mark feedback as complete once satisfied</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-900/20 border-2 border-amber-500/30 rounded-2xl p-8 backdrop-blur-sm">
              <div className="inline-flex p-3 bg-amber-500 rounded-xl mb-4">
                <User size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">For Managers</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">View all feedback requests from team members</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Provide structured feedback with ratings and comments</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Revise feedback based on employee requests</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Export completed feedback as professional PDFs</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">Access team analytics and feedback trends</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-3xl p-12 text-white border-2 border-slate-600 shadow-2xl backdrop-blur-sm">
            <h2 className="text-4xl font-bold mb-4 heading-font">Ready to Get Started?</h2>
            <p className="text-xl mb-8 text-slate-300">
              Experience transparent, efficient feedback management that brings teams closer together.
            </p>
            <button 
              onClick={login}
              className="inline-flex items-center gap-0 px-10 py-5 bg-black hover:bg-gray-800 text-white text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl group focus:outline-none focus:ring-4 focus:ring-gray-300"
              aria-label="Sign in with Centific"
            >
              <span>Sign in with</span>
              <CentificLogo className="h-11" />
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
